import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IndianRupee, Users, AlertTriangle, FileSignature, CalendarDays, Wallet } from "lucide-react";
import { format } from "date-fns";
import { formatLakhsOrCr } from "@/lib/utils";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Card } from "@/components/ui/card";

import { HeroKPICard } from "@/components/admin-dashboard/HeroKPICard";
import { ActionItemsCard, type ActionItem } from "@/components/admin-dashboard/ActionItemsCard";
import { SanctionLimitCard } from "@/components/admin-dashboard/SanctionLimitCard";
import { AstrafinCashflowChart } from "@/components/admin-dashboard/AstrafinCashflowChart";
import { AdminDashboardMap } from "@/components/admin-dashboard/AdminDashboardMap";
import { DashboardBreakdownTable } from "@/components/admin-dashboard/DashboardBreakdownTable";
import { DashboardLineChart } from "@/components/admin-dashboard/DashboardLineChart";
import { forecastDashboardRanges, type DashboardRange } from "@/lib/dashboard-analytics";
import {
  EMPTY_ADMIN_DASHBOARD_KPIS,
  normalizeAdminDashboardKpis,
  type AdminDashboardKpis,
} from "@/lib/admin-dashboard-kpis";
import {
  EMPTY_DISBURSEMENT_TREND,
  normalizeAdminDashboardDisbursementTrend,
  type AdminDashboardDisbursementTrend,
} from "@/lib/admin-dashboard-disbursement";
import {
  normalizeActionItems,
  normalizeRefundableSecurityTrend,
  normalizeSigningTrend,
  type RefundableSecurityDetailRow,
  type RefundableSecuritySeriesPoint,
  type SigningSeriesPoint,
} from "@/lib/admin-dashboard-charts";
import apiService from "@/services/apiService";

const formatDetailDate = (value: string) => {
  if (!value) return "—";
  const d = new Date(
    /^\d{4}-\d{2}-\d{2}/.test(value) ? `${value.slice(0, 10)}T00:00:00` : value
  );
  return Number.isNaN(d.getTime()) ? value : format(d, "dd MMM yyyy");
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [disbursementRange, setDisbursementRange] = useState<DashboardRange>("current_fy");
  const [signingRange, setSigningRange] = useState<DashboardRange>("current_fy");
  const [refundableSdRange, setRefundableSdRange] = useState<DashboardRange>("current_fy");
  const [heroKPIs, setHeroKPIs] = useState<AdminDashboardKpis>(EMPTY_ADMIN_DASHBOARD_KPIS);
  const [heroKpisLoading, setHeroKpisLoading] = useState(true);
  const [disbursementTrend, setDisbursementTrend] = useState<AdminDashboardDisbursementTrend>(EMPTY_DISBURSEMENT_TREND);
  const [disbursementLoading, setDisbursementLoading] = useState(true);
  const [signingData, setSigningData] = useState<SigningSeriesPoint[]>([]);
  const [signingLoading, setSigningLoading] = useState(true);
  const [refundableSdData, setRefundableSdData] = useState<RefundableSecuritySeriesPoint[]>([]);
  const [refundableSdDetails, setRefundableSdDetails] = useState<RefundableSecurityDetailRow[]>([]);
  const [refundableSdLoading, setRefundableSdLoading] = useState(true);
  const [actionItems, setActionItems] = useState<ActionItem[]>([]);
  const [actionItemsLoading, setActionItemsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setHeroKpisLoading(true);
      try {
        const response = await apiService.get<unknown>("/rental-schedule/admin-dashboard-kpis");
        if (cancelled) return;
        setHeroKPIs(normalizeAdminDashboardKpis(response));
      } catch (error) {
        console.error("Failed to load admin dashboard KPIs:", error);
        if (!cancelled) setHeroKPIs(EMPTY_ADMIN_DASHBOARD_KPIS);
      } finally {
        if (!cancelled) setHeroKpisLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setDisbursementLoading(true);
      try {
        const response = await apiService.get<unknown>(
          "/rental-schedule/admin-dashboard-disbursement-trend",
          { params: { range: disbursementRange, mode: "amount" } }
        );
        if (cancelled) return;
        setDisbursementTrend(
          normalizeAdminDashboardDisbursementTrend(response, disbursementRange, "amount")
        );
      } catch (error) {
        console.error("Failed to load disbursement trend:", error);
        if (!cancelled) {
          setDisbursementTrend({
            ...EMPTY_DISBURSEMENT_TREND,
            range: disbursementRange,
            mode: "amount",
          });
        }
      } finally {
        if (!cancelled) setDisbursementLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [disbursementRange]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setSigningLoading(true);
      try {
        const response = await apiService.get<unknown>(
          "/rental-schedule/admin-dashboard-signing-trend",
          { params: { range: signingRange } }
        );
        if (cancelled) return;
        setSigningData(normalizeSigningTrend(response));
      } catch (error) {
        console.error("Failed to load signing trend:", error);
        if (!cancelled) setSigningData([]);
      } finally {
        if (!cancelled) setSigningLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [signingRange]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setRefundableSdLoading(true);
      try {
        const response = await apiService.get<unknown>(
          "/rental-schedule/admin-dashboard-refundable-security-trend",
          { params: { range: refundableSdRange } }
        );
        if (cancelled) return;
        const parsed = normalizeRefundableSecurityTrend(response);
        setRefundableSdData(parsed.series);
        setRefundableSdDetails(parsed.details);
      } catch (error) {
        console.error("Failed to load refundable security deposit trend:", error);
        if (!cancelled) {
          setRefundableSdData([]);
          setRefundableSdDetails([]);
        }
      } finally {
        if (!cancelled) setRefundableSdLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [refundableSdRange]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setActionItemsLoading(true);
      try {
        const response = await apiService.get<unknown>("/rental-schedule/admin-dashboard-action-items");
        if (cancelled) return;
        setActionItems(normalizeActionItems(response));
      } catch (error) {
        console.error("Failed to load action items:", error);
        if (!cancelled) setActionItems([]);
      } finally {
        if (!cancelled) setActionItemsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const disbursementData = disbursementTrend.series;
  const disbursementDetails = disbursementTrend.details;

  return (
    <div className="dashboard-shell space-y-5 bg-white p-3 sm:p-4 lg:p-5 w-full">
      <section aria-label="Portfolio indicators">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6">
          {heroKpisLoading
            ? Array.from({ length: 6 }).map((_, index) => (
                <Card key={index} className="h-[110px] border p-4">
                  <Skeleton className="h-3 w-24" />
                  <Skeleton className="mt-6 h-7 w-20" />
                  <Skeleton className="mt-2 h-3 w-28" />
                </Card>
              ))
            : (
              <>
                <HeroKPICard title="Assets Under Management" value={formatLakhsOrCr(heroKPIs.aum)} subtitle={`Across ${heroKPIs.uniqueCustomers} customer${heroKPIs.uniqueCustomers !== 1 ? "s" : ""}`} icon={<IndianRupee className="h-4 w-4" />} variant="default" />
                <HeroKPICard title="MRA Signed" value={heroKPIs.mraSignedCount} subtitle={`of ${heroKPIs.totalCustomers} customer${heroKPIs.totalCustomers !== 1 ? "s" : ""}`} icon={<FileSignature className="h-4 w-4" />} variant="info" />
                <HeroKPICard title="Active Customers" value={heroKPIs.activeCustomers} subtitle={`of ${heroKPIs.totalCustomers} total customer${heroKPIs.totalCustomers !== 1 ? "s" : ""}`} icon={<Users className="h-4 w-4" />} variant="success" />
                <HeroKPICard title="Due This Month" value={formatLakhsOrCr(heroKPIs.amountDueThisMonth)} subtitle={`${heroKPIs.dueThisMonthCount} invoice${heroKPIs.dueThisMonthCount !== 1 ? "s" : ""}`} icon={<CalendarDays className="h-4 w-4" />} variant="warning" />
                <HeroKPICard title="Cashflow to Astrafin" value={formatLakhsOrCr(heroKPIs.cashflowToAstrafin)} subtitle="Self-funded receipts" icon={<Wallet className="h-4 w-4" />} variant="success" />
                <HeroKPICard title="Total Outstanding" value={formatLakhsOrCr(heroKPIs.totalOutstanding)} subtitle={`${heroKPIs.unpaidCount} unpaid invoices`} icon={<AlertTriangle className="h-4 w-4" />} variant="danger" />
              </>
            )}
        </div>
      </section>

      <section aria-label="Disbursement and capacity analytics">
        <div className="grid grid-cols-1 gap-5 xl:grid-cols-12">
          <div className="xl:col-span-8">
            <DashboardLineChart
              eyebrow="Customer mix"
              title="New vs Repeat Disbursement"
              data={disbursementData}
              loading={disbursementLoading}
              valueType="currency"
              detailDescription="New means the customer's first approved/active schedule; later schedules are repeat. Dates use post-disbursement date, falling back to schedule creation."
              details={<div className="overflow-x-auto"><Table><TableHeader><TableRow><TableHead>Period</TableHead><TableHead>Customer Name</TableHead><TableHead className="text-right">Amount</TableHead><TableHead>Status</TableHead></TableRow></TableHeader><TableBody>{disbursementLoading ? <TableRow><TableCell colSpan={4} className="py-8 text-center text-muted-foreground">Loading…</TableCell></TableRow> : <>{disbursementDetails.map(record => <TableRow key={record.id}><TableCell className="whitespace-nowrap">{record.date ? format(new Date(`${record.date}T00:00:00`), "dd MMM yyyy") : "—"}</TableCell><TableCell>{record.customerName}</TableCell><TableCell className="text-right tabular-nums">{formatLakhsOrCr(Math.round(record.amount))}</TableCell><TableCell><Badge variant={record.status === "New" ? "success" : "secondary"}>{record.status}</Badge></TableCell></TableRow>)}{disbursementDetails.length === 0 && <TableRow><TableCell colSpan={4} className="py-8 text-center text-muted-foreground">No disbursements in this period</TableCell></TableRow>}</>}</TableBody></Table></div>}
              range={disbursementRange}
              onRangeChange={setDisbursementRange}
              series={[
                { key: "newCustomer", label: "New Customers", color: "hsl(var(--primary))" },
                { key: "repeatCustomer", label: "Repeat Customers", color: "hsl(var(--chart-gold))" },
              ]}
            />
          </div>
          <div className="xl:col-span-4"><SanctionLimitCard /></div>
        </div>
      </section>

      <section aria-label="Agreement and refundable security trends">
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          <DashboardLineChart
            eyebrow="Documentation velocity"
            title="MRA Signed"
            detailDescription="MRA counts are deduplicated by customer and signed date. Termsheets use the recorded signed document date."
            details={<DashboardBreakdownTable data={signingData} columns={[{ key: "mra", label: "MRA Signed" }, { key: "termsheet", label: "Termsheet Signed" }]} currency={false} />}
            data={signingData}
            loading={signingLoading}
            range={signingRange}
            onRangeChange={setSigningRange}
            valueType="count"
            series={[
              { key: "mra", label: "MRA Signed", color: "hsl(var(--primary))" },
            ]}
          />
          <DashboardLineChart
            eyebrow="End-of-tenure exposure"
            title="Refundable Security Deposit"
            rangeOptions={forecastDashboardRanges}
            showInfo
            detailDescription="Customer-wise refundable security deposit with buyback date for the selected period."
            details={
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Customer Name</TableHead>
                      <TableHead>Rental Schedule</TableHead>
                      <TableHead>Buyback Date</TableHead>
                      <TableHead className="text-right">Refundable Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {refundableSdLoading ? (
                      <TableRow>
                        <TableCell
                          colSpan={3}
                          className="py-8 text-center text-muted-foreground"
                        >
                          Loading…
                        </TableCell>
                      </TableRow>
                    ) : (
                      <>
                        {refundableSdDetails.map((row) => (
                          <TableRow key={row.id}>
                            <TableCell>{row.customerName || "—"}</TableCell>
                            <TableCell className="whitespace-nowrap">
                              {row.rentalSchedule || "—"}
                            </TableCell>
                            <TableCell className="whitespace-nowrap">
                              {formatDetailDate(row.buybackDate)}
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                              {formatLakhsOrCr(row.refundableAmount)}
                            </TableCell>
                          </TableRow>
                        ))}
                        {refundableSdDetails.length === 0 && (
                          <TableRow>
                            <TableCell
                              colSpan={3}
                              className="py-8 text-center text-muted-foreground"
                            >
                              No refundable security deposits in this period
                            </TableCell>
                          </TableRow>
                        )}
                      </>
                    )}
                  </TableBody>
                </Table>
              </div>
            }
            data={refundableSdData}
            loading={refundableSdLoading}
            range={refundableSdRange}
            onRangeChange={setRefundableSdRange}
            series={[
              {
                key: "amount",
                label: "Refundable Security Deposit",
                color: "hsl(var(--primary))",
              },
            ]}
          />
        </div>
      </section>

      <section aria-label="Cashflow and action items">
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <AstrafinCashflowChart />
          </div>
          <div className="lg:col-span-5">
            <ActionItemsCard
              items={actionItems}
              loading={actionItemsLoading}
              onItemClick={() => { navigate("/manage-post-disbursement"); }}
            />
          </div>
        </div>
      </section>

      <section aria-label="Geographic map">
        <AdminDashboardMap />
      </section>
    </div>
  );
};

export default AdminDashboard;
